import { Component, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { Storage } from '@ionic/storage';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Geolocation } from '@capacitor/geolocation';
import { Keyboard } from '@capacitor/keyboard';
import { Browser } from '@capacitor/browser';
import { SplashScreen } from '@capacitor/splash-screen';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { FilePicker } from '@capawesome/capacitor-file-picker';
import { Exif as CapacitorExif } from '@capacitor-community/exif';
import { Util } from 'src/shared/util/util';
import OneSignal from 'onesignal-cordova-plugin';

@Component({
  selector: 'capacitor-test-modal',
  templateUrl: './capacitor-test-modal.html',
  styleUrls: ['./capacitor-test-modal.scss'],
  standalone: false
})
export class CapacitorTestModal implements OnInit {
  public platform = '';
  public storage: Storage;
  public testResults: any[] = [];

  constructor(
    private modalController: ModalController,
    public util: Util,
    storageService: Storage
  ) {
    this.storage = storageService;
    this.platform = Capacitor.getPlatform();
  }

  async ngOnInit() {
    this.testResults = [];
    await this.storage.create();
    await this.testPlatformInfo();
  }

  dismiss() {
    this.modalController.dismiss();
  }

  async runAllTests() {
    this.testResults = [];
    this.util.showInfo('Running all automated tests...');

    await this.testPlatformInfo();
    await this.testAppInfo();
    await this.testStorage();
    await this.testGeolocation();
    await this.testNotifications();
    await this.testKeyboard();
    await this.testSplashScreen();
    await this.testBrowser();
    await this.testVideoRecorder();
    await this.testCamera();
    await this.testGallery();
    await this.testMultiplePhotos();
    await this.testFilePicker();
    await this.testExifData();

    this.util.showSuccess('All tests completed!');
  }

  clearResults() {
    this.testResults = [];
    this.util.showInfo('Results cleared');
  }

  async testPlatformInfo() {
    try {
      const platform = Capacitor.getPlatform();
      const isNative = Capacitor.isNativePlatform();

      this.testResults.push({
        test: 'Platform Info',
        status: 'success',
        message: `Platform: ${platform}, Native: ${isNative}`
      });
    } catch (error) {
      this.testResults.push({
        test: 'Platform Info',
        status: 'error',
        message: error.message || 'Failed'
      });
    }
  }

  async testAppInfo() {
    try {
      const appInfo = await App.getInfo();
      this.testResults.push({
        test: 'App Info',
        status: 'success',
        message: `${appInfo.name} v${appInfo.version} (${appInfo.build})`
      });
      this.util.showSuccess('App info test completed');
    } catch (error) {
      this.testResults.push({
        test: 'App Info',
        status: 'error',
        message: error.message || 'Failed'
      });
    }
  }

  async testCamera() {
    try {
      const photo = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Uri,
        source: CameraSource.Photos
      });

      this.testResults.push({
        test: 'Camera',
        status: 'success',
        message: `Photo selected: ${photo.format}`
      });
      this.util.showSuccess('Camera test completed');
    } catch (error) {
      this.testResults.push({
        test: 'Camera',
        status: 'error',
        message: error.message || 'Failed'
      });
    }
  }

  async testGallery() {
    try {
      const _photo = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Uri,
        source: CameraSource.Photos
      });

      this.testResults.push({
        test: 'Gallery',
        status: 'success',
        message: 'Photo selected successfully'
      });
      this.util.showSuccess('Gallery test completed');
    } catch (error) {
      this.testResults.push({
        test: 'Gallery',
        status: 'error',
        message: error.message || 'Failed'
      });
    }
  }

  async testMultiplePhotos() {
    try {
      const photos = await Camera.pickImages({
        quality: 75,
        limit: 5
      });

      this.testResults.push({
        test: 'Multiple Photos',
        status: 'success',
        message: `Selected ${photos.photos.length} photos`
      });
      this.util.showSuccess('Multiple photos test completed');
    } catch (error) {
      this.testResults.push({
        test: 'Multiple Photos',
        status: 'error',
        message: error.message || 'Failed'
      });
    }
  }

  async testGeolocation() {
    try {
      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      });

      this.testResults.push({
        test: 'Geolocation',
        status: 'success',
        message: `Lat: ${position.coords.latitude.toFixed(6)}, Lon: ${position.coords.longitude.toFixed(6)}, Accuracy: ${position.coords.accuracy.toFixed(2)}m`
      });
      this.util.showSuccess('Geolocation test completed');
    } catch (error) {
      this.testResults.push({
        test: 'Geolocation',
        status: 'error',
        message: error.message || 'Failed'
      });
    }
  }

  async testFilePicker() {
    try {
      const result = await FilePicker.pickFiles({ readData: true });

      if (result.files && result.files.length > 0) {
        const file = result.files[0];
        this.testResults.push({
          test: 'File Picker',
          status: 'success',
          message: `File: ${file.name} (${file.size} bytes)`
        });
        this.util.showSuccess('File picker test completed');
      }
    } catch (error) {
      this.testResults.push({
        test: 'File Picker',
        status: 'error',
        message: error.message || 'Failed'
      });
    }
  }

  async testKeyboard() {
    try {
      if (!Capacitor.isNativePlatform()) {
        this.testResults.push({
          test: 'Keyboard',
          status: 'skipped',
          message: 'Only available on native platforms'
        });
        return;
      }

      await Keyboard.show();
      await new Promise(resolve => setTimeout(resolve, 500));
      await Keyboard.hide();

      this.testResults.push({
        test: 'Keyboard',
        status: 'success',
        message: 'Show/hide successful'
      });
      this.util.showSuccess('Keyboard test completed');
    } catch (error) {
      // Keyboard.show() returns "not implemented" on iOS simulator
      if (error.message === 'not implemented') {
        this.testResults.push({
          test: 'Keyboard',
          status: 'skipped',
          message: 'Requires physical device (not available in simulator)'
        });
        this.util.showInfo('Keyboard test skipped (requires physical device)');
      } else {
        this.testResults.push({
          test: 'Keyboard',
          status: 'error',
          message: error.message || 'Failed'
        });
      }
    }
  }

  async testBrowser() {
    try {
      await Browser.open({ url: 'https://capacitorjs.com', presentationStyle: 'popover' });

      setTimeout(async () => {
        await Browser.close();
      }, 500);

      this.testResults.push({
        test: 'Browser',
        status: 'success',
        message: 'Open/close successful'
      });
      this.util.showSuccess('Browser test completed');
    } catch (error) {
      this.testResults.push({
        test: 'Browser',
        status: 'error',
        message: error.message || 'Failed'
      });
    }
  }

  async testExifData() {
    try {
      const photo = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Uri,
        source: CameraSource.Photos
      });

      if (!photo.path) {
        this.testResults.push({
          test: 'EXIF',
          status: 'error',
          message: 'No path available for EXIF reading'
        });
        return;
      }

      const exifData = await CapacitorExif.getCoordinates({ pathToImage: photo.path });

      this.testResults.push({
        test: 'EXIF',
        status: 'success',
        message: `Read ${Object.keys(exifData).length} EXIF fields`
      });
      this.util.showSuccess('EXIF test completed');
    } catch (error) {
      this.testResults.push({
        test: 'EXIF',
        status: 'error',
        message: error.message || 'Failed'
      });
    }
  }

  async testStorage() {
    try {
      const testKey = 'capacitor_test_key';
      const testValue = { data: 'test', timestamp: Date.now() };

      await this.storage.set(testKey, testValue);
      const _retrieved = await this.storage.get(testKey);
      await this.storage.remove(testKey);

      this.testResults.push({
        test: 'Storage',
        status: 'success',
        message: 'Write/read/delete successful'
      });
      this.util.showSuccess('Storage test completed');
    } catch (error) {
      this.testResults.push({
        test: 'Storage',
        status: 'error',
        message: error.message || 'Failed'
      });
    }
  }

  async testNotifications() {
    try {
      if (!Capacitor.isNativePlatform()) {
        this.testResults.push({
          test: 'Notifications',
          status: 'skipped',
          message: 'Only available on native platforms'
        });
        return;
      }

      const isInitialized = typeof OneSignal !== 'undefined';

      this.testResults.push({
        test: 'Notifications (OneSignal)',
        status: isInitialized ? 'success' : 'error',
        message: `SDK ${isInitialized ? 'initialized' : 'not initialized'}`
      });
      this.util.showSuccess('Notifications test completed');
    } catch (error) {
      this.testResults.push({
        test: 'Notifications',
        status: 'error',
        message: error.message || 'Failed'
      });
    }
  }

  async testSplashScreen() {
    try {
      if (!Capacitor.isNativePlatform()) {
        this.testResults.push({
          test: 'Splash Screen',
          status: 'skipped',
          message: 'Only available on native platforms'
        });
        return;
      }

      await SplashScreen.show({ autoHide: false });
      await new Promise(resolve => setTimeout(resolve, 500));
      await SplashScreen.hide();

      this.testResults.push({
        test: 'Splash Screen',
        status: 'success',
        message: 'Show/hide successful'
      });
      this.util.showSuccess('Splash screen test completed');
    } catch (error) {
      this.testResults.push({
        test: 'Splash Screen',
        status: 'error',
        message: error.message || 'Failed'
      });
    }
  }

  async testVideoRecorder() {
    // VideoRecorder requires a camera preview view - can't test programmatically
    this.testResults.push({
      test: 'Video Recorder',
      status: 'skipped',
      message: 'Requires camera preview UI (cannot test programmatically)'
    });
    this.util.showInfo('Video recorder test skipped (requires UI setup)');
  }

  trackByResult(index: number, result: any): any {
    return result?.test || index;
  }
}
